import { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus, FiSearch, FiTrash2, FiX } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import ExportExcelButton from "../../../components/common/ExportExcelButton";
import { downloadExcelRows } from "../../../utils/downloadExcel";
import "../../../styles/users.css";
import "../../../styles/ssdiLinkBanker.css";

function dash(value) {
  const text = String(value || "").trim();
  return text || "—";
}

function formatLinkedOn(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function statusClass(status) {
  const key = String(status || "").toLowerCase();
  if (key === "active" || key === "pending") return key;
  return "inactive";
}

function matchesQuery(haystack, query) {
  if (!query) return true;
  return String(haystack || "")
    .toLowerCase()
    .includes(query);
}

function SearchablePicker({
  id,
  label,
  query,
  onQueryChange,
  placeholder,
  options,
  value,
  onChange,
  emptyText,
}) {
  const selected = options.find((item) => item.id === value) || null;
  const q = String(query || "").trim().toLowerCase();
  const visible = q
    ? options.filter((item) => matchesQuery(item.searchText || item.title, q))
    : options;

  return (
    <div className="sla-picker">
      <label htmlFor={id}>{label}</label>
      {selected ? (
        <div className="sla-picker__chosen">
          <div className="sla-picker__chosen-text">
            <strong>{selected.title}</strong>
            {selected.subtitle ? <small>{selected.subtitle}</small> : null}
          </div>
          <button
            type="button"
            className="sla-picker__change"
            onClick={() => {
              onChange("");
              onQueryChange("");
            }}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="sla-picker__search">
            <FiSearch aria-hidden />
            <input
              id={id}
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={placeholder}
              autoComplete="off"
            />
          </div>
          <ul className="sla-picker__list" role="listbox" aria-label={label}>
            {visible.length === 0 ? (
              <li className="sla-picker__empty">{emptyText}</li>
            ) : (
              visible.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="sla-picker__option"
                    onClick={() => onChange(item.id)}
                  >
                    <strong>{item.title}</strong>
                    {item.subtitle ? <small>{item.subtitle}</small> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </>
      )}
    </div>
  );
}

function AgencyUsers({ agency }) {
  return (
    <div className="sla-users">
      <strong>{Number(agency.totalUsers) || 0}</strong>
      <small>
        Active {Number(agency.activeUsers) || 0} · Inactive {Number(agency.inactiveUsers) || 0}
      </small>
    </div>
  );
}

function AgencyActions({ agency, removingId, onUnlink }) {
  return (
    <div className="sla-row-actions">
      <button
        type="button"
        className="company-btn company-btn--delete"
        disabled={removingId === agency.linkId}
        onClick={() => onUnlink(agency)}
      >
        <FiTrash2 aria-hidden />
        <span>{removingId === agency.linkId ? "Unlinking…" : "Unlink"}</span>
      </button>
    </div>
  );
}

export default function SsdiLinkBankerAgency({ bank }) {
  const bankId = bank?._id || bank?.id || "";
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [removingId, setRemovingId] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [agencyQuery, setAgencyQuery] = useState("");
  const [selectedAgencyId, setSelectedAgencyId] = useState("");
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState("");

  const loadAgencies = useCallback(async () => {
    if (!bankId) return;
    const res = await bankService.ssdiLinkedAgencies(bankId);
    setAgencies(res?.data?.data?.linkedAgencies || []);
  }, [bankId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    loadAgencies()
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || "Failed to load linked agencies.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadAgencies]);

  useEffect(() => {
    if (!createOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !linkSaving) setCreateOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createOpen, linkSaving]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agencies;
    return agencies.filter((agency) =>
      [
        agency.name,
        agency.code,
        agency.adminName,
        agency.adminEmail,
        agency.phone,
        agency.address || agency.location,
        agency.pincode,
        agency.status,
      ].some((field) => matchesQuery(field, q))
    );
  }, [agencies, search]);

  const agencyOptions = useMemo(
    () =>
      available.map((agency) => ({
        id: String(agency.id),
        title: agency.name || "Agency",
        subtitle: [agency.code, agency.adminName, agency.adminEmail].filter(Boolean).join(" — "),
        searchText: [agency.name, agency.code, agency.adminName, agency.adminEmail].join(" "),
      })),
    [available]
  );

  const openCreate = async () => {
    setLinkError("");
    setNotice("");
    setAgencyQuery("");
    setSelectedAgencyId("");
    setCreateOpen(true);
    setAvailableLoading(true);
    try {
      const res = await bankService.ssdiAvailableAgencies(bankId);
      setAvailable(res?.data?.data?.agencies || []);
    } catch (err) {
      setLinkError(err?.response?.data?.message || "Failed to load agencies.");
      setAvailable([]);
    } finally {
      setAvailableLoading(false);
    }
  };

  const handleCreateLink = async (event) => {
    event.preventDefault();
    setLinkError("");
    setNotice("");
    if (!selectedAgencyId) {
      setLinkError("Select an agency.");
      return;
    }
    setLinkSaving(true);
    try {
      const res = await bankService.ssdiCreateLink({
        bankId: String(bankId),
        agencyId: selectedAgencyId,
      });
      setCreateOpen(false);
      setSelectedAgencyId("");
      setAgencyQuery("");
      await loadAgencies();
      setNotice(
        res?.data?.message || `Agency linked successfully with ${bank?.bankName || "this bank"}.`
      );
    } catch (err) {
      setLinkError(err?.response?.data?.message || "Failed to create link.");
    } finally {
      setLinkSaving(false);
    }
  };

  const handleUnlink = async (agency) => {
    const label = agency.name || "this agency";
    if (!window.confirm(`Unlink ${label} from ${bank?.bankName || "this bank"}?`)) return;
    setRemovingId(agency.linkId);
    setError("");
    try {
      await bankService.ssdiDeleteLink(agency.linkId);
      await loadAgencies();
      setNotice(`${label} unlinked.`);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to unlink agency.");
    } finally {
      setRemovingId("");
    }
  };

  const countLabel = `${agencies.length} ${agencies.length === 1 ? "Agency" : "Agencies"} Linked`;

  const downloadAgencies = () => {
    const headers = [
      "Agency Name",
      "Agency Code",
      "Admin Name",
      "Admin Email",
      "Phone",
      "Address",
      "Pincode",
      "Total Users",
      "Active Users",
      "Inactive Users",
      "Status",
      "Linked On",
    ];
    const rows = agencies.map((agency) => ({
      "Agency Name": agency.name || "",
      "Agency Code": agency.code || "",
      "Admin Name": agency.adminName || "",
      "Admin Email": agency.adminEmail || "",
      Phone: agency.phone || "",
      Address: agency.address || agency.location || "",
      Pincode: agency.pincode || "",
      "Total Users": Number(agency.totalUsers) || 0,
      "Active Users": Number(agency.activeUsers) || 0,
      "Inactive Users": Number(agency.inactiveUsers) || 0,
      Status: agency.status || "",
      "Linked On": formatLinkedOn(agency.linkedAt) === "—" ? "" : formatLinkedOn(agency.linkedAt),
    }));
    const code = String(bank?.bankCode || bankId || "bank").replace(/[^\w.-]+/g, "");
    downloadExcelRows(rows, `linked-agencies-${code}.xlsx`, headers);
  };

  return (
    <section
      className={`create-company-card ssdi-bank-details-card sla-card${
        agencies.length > 0 ? " sla-card--filled" : ""
      }`}
    >
      <header className="sla-head">
        <div>
          <h3>Linked Agencies</h3>
          <p className="sla-stats">{countLabel}</p>
        </div>
        <div className="sla-head-actions">
          <ExportExcelButton onClick={downloadAgencies} label="Download Excel" />
          <button type="button" className="primary-page-btn" onClick={openCreate}>
            <FiPlus aria-hidden />
            Link Agency
          </button>
        </div>
      </header>

      {agencies.length > 0 ? (
        <div className="company-search-panel company-search-panel--simple sla-search">
          <div className="company-search-bar">
            <FiSearch className="company-search-icon" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agency, code, admin, email, or phone…"
              aria-label="Search linked agencies"
            />
          </div>
        </div>
      ) : null}

      {notice ? <p className="sla-notice">{notice}</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      <div className="company-table-wrap ssdi-excel-sheet table-scroll sla-table-wrap">
        <table className="users-table excel-grid-table sla-table">
          <thead>
            <tr>
              <th>Agency Name</th>
              <th>Agency Code</th>
              <th>Admin Name</th>
              <th>Admin Email</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Pincode</th>
              <th>Users</th>
              <th>Status</th>
              <th>Linked On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11}>Loading linked agencies…</td>
              </tr>
            ) : agencies.length === 0 ? (
              <tr>
                <td colSpan={11}>No agencies linked with this bank yet.</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={11}>No linked agencies match your search.</td>
              </tr>
            ) : (
              filtered.map((agency) => (
                    <tr key={agency.linkId || agency.id}>
                      <td>{dash(agency.name)}</td>
                      <td>{dash(agency.code)}</td>
                      <td>{dash(agency.adminName)}</td>
                      <td>{dash(agency.adminEmail)}</td>
                      <td>{dash(agency.phone)}</td>
                      <td className="sla-address-td">
                        <span className="sla-address" title={agency.address || agency.location || ""}>
                          {dash(agency.address || agency.location)}
                        </span>
                      </td>
                      <td>{dash(agency.pincode)}</td>
                      <td>
                        <AgencyUsers agency={agency} />
                      </td>
                      <td>
                        <span className={`company-status company-status--${statusClass(agency.status)}`}>
                          {dash(agency.status)}
                        </span>
                      </td>
                      <td>{formatLinkedOn(agency.linkedAt)}</td>
                      <td>
                        <AgencyActions
                          agency={agency}
                          removingId={removingId}
                          onUnlink={handleUnlink}
                        />
                      </td>
                    </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {agencies.length > 0 ? (
        <div className="sla-cards">
          {filtered.length === 0 ? (
            <p className="muted">No linked agencies match your search.</p>
          ) : (
            filtered.map((agency) => (
                <article key={`card-${agency.linkId || agency.id}`} className="sla-agency-card">
                  <header>
                    <h4>{dash(agency.name)}</h4>
                    <span className={`company-status company-status--${statusClass(agency.status)}`}>
                      {dash(agency.status)}
                    </span>
                  </header>
                  <dl>
                    <div>
                      <dt>Code</dt>
                      <dd>{dash(agency.code)}</dd>
                    </div>
                    <div>
                      <dt>Admin</dt>
                      <dd>{dash(agency.adminName)}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{dash(agency.adminEmail)}</dd>
                    </div>
                    <div>
                      <dt>Phone</dt>
                      <dd>{dash(agency.phone)}</dd>
                    </div>
                    <div>
                      <dt>Address</dt>
                      <dd className="sla-address" title={agency.address || agency.location || ""}>
                        {dash(agency.address || agency.location)}
                      </dd>
                    </div>
                    <div>
                      <dt>Pincode</dt>
                      <dd>{dash(agency.pincode)}</dd>
                    </div>
                    <div>
                      <dt>Users</dt>
                      <dd>
                        <AgencyUsers agency={agency} />
                      </dd>
                    </div>
                    <div>
                      <dt>Linked on</dt>
                      <dd>{formatLinkedOn(agency.linkedAt)}</dd>
                    </div>
                  </dl>
                  <AgencyActions agency={agency} removingId={removingId} onUnlink={handleUnlink} />
                </article>
            ))
          )}
        </div>
      ) : null}

      {createOpen ? (
        <div
          className="modal-overlay"
          role="presentation"
          onClick={() => !linkSaving && setCreateOpen(false)}
        >
          <div
            className="modal-box sla-modal sla-modal--create"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sla-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sla-modal__head">
              <div>
                <h3 id="sla-create-title">Create Link</h3>
                <p className="sla-modal__hint">This bank is already selected. Choose an agency.</p>
              </div>
              <button
                type="button"
                className="sla-close"
                onClick={() => !linkSaving && setCreateOpen(false)}
                aria-label="Close create link"
                disabled={linkSaving}
              >
                <FiX aria-hidden />
              </button>
            </div>
            <form className="sla-create-form" onSubmit={handleCreateLink}>
              <div className="sla-picker">
                <label id="sla-banker-label">Banker</label>
                <div className="sla-banker" role="group" aria-labelledby="sla-banker-label">
                  <div className="sla-banker__text">
                    <strong>{dash(bank?.bankName)}</strong>
                    <small>
                      {[bank?.bankCode, bank?.email].filter(Boolean).join(" · ") || "—"}
                    </small>
                  </div>
                  <span className="sla-banker__badge">Selected</span>
                </div>
              </div>
              {availableLoading ? (
                <p className="muted">Loading agencies…</p>
              ) : (
                <SearchablePicker
                  id="sla-agency-search"
                  label="Agency / Repo Admin"
                  query={agencyQuery}
                  onQueryChange={setAgencyQuery}
                  placeholder="Search agency name, code, admin, or email"
                  options={agencyOptions}
                  value={selectedAgencyId}
                  onChange={setSelectedAgencyId}
                  emptyText={
                    available.length
                      ? "No agencies match your search."
                      : "No agencies available to link."
                  }
                />
              )}
              {linkError ? <p className="error-text">{linkError}</p> : null}
              <div className="sla-create-actions">
                <button
                  type="button"
                  className="secondary-page-btn"
                  onClick={() => setCreateOpen(false)}
                  disabled={linkSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-page-btn" disabled={linkSaving || availableLoading}>
                  {linkSaving ? "Creating…" : "Create Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}
